<script setup lang="ts">
import { FloatMenuContextKey } from '../FloatMenuContext'

interface Props {
  menuId?: string
  closeOnSelect?: boolean
  isOpen?: boolean
}

const props = defineProps<Props>()

const emits = defineEmits<Emits>()

const id = props.menuId ?? randomID()
const itensClass = (`menuitem-${id}`)

const isOpen = toRef(props, 'isOpen')

interface Emits {
  (event: 'onSelect', value: unknown, ignoreClose?: boolean): boolean
  (event: 'toggle', value?: boolean): void
}

const toggle = (value?: boolean) => {
  emits('toggle', value)
}

const onSelect = (value: unknown, ignoreClose = false) => {
  if (props.closeOnSelect && !ignoreClose)
    toggle(false)
  emits('onSelect', value, ignoreClose)
}

provide(FloatMenuContextKey, {
  id,
  onSelect,
  isOpen,
  toggle,
  itensClass,
})

const chilrens = ref<HTMLElement[]>([])
const wrapperRef = ref<HTMLUListElement | null>(null)
const loadMenuControls = () => {
  const container = wrapperRef.value
  if (!container)
    return
  const menuitems = container.querySelectorAll(`.${itensClass}`) as NodeListOf<HTMLElement>
  if (menuitems.length)
    chilrens.value = Array.from(menuitems)

  chilrens.value.forEach((item, index) => {
    item.tabIndex = index + 1
  })
}
const currentChildIndex = ref(-1)

function moveToNext() {
  const next = currentChildIndex.value + 1
  const nextIndex = next < chilrens.value.length ? next : 0
  currentChildIndex.value = nextIndex
  chilrens.value[nextIndex].focus()
}

function moveToPrevious() {
  const next = currentChildIndex.value - 1
  const nextIndex = next >= 0 ? next : chilrens.value.length - 1
  currentChildIndex.value = nextIndex
  chilrens.value[nextIndex].focus()
}

useEventListener('mousedown', (event) => {
  if (isOpen.value && wrapperRef.value) {
    if (!wrapperRef.value.contains(event.target as Node))
      toggle(false)
  }
})

useEventListener('keydown', (event) => {
  if (isOpen.value && wrapperRef.value) {
    if (event.key.toLowerCase() === 'escape')
      return toggle(false)
    if (event.key.toLowerCase() === 'arrowdown' || (event.key.toLowerCase() === 'tab' && !event.shiftKey)) {
      event.preventDefault()
      return moveToNext()
    }

    if (event.key.toLowerCase() === 'arrowup' || (event.key.toLowerCase() === 'tab' && event.shiftKey)) {
      event.preventDefault()
      return moveToPrevious()
    }

    if (event.key.toLowerCase() === 'enter' || event.key.toLowerCase() === 'space') {
      event.preventDefault()
      if (currentChildIndex.value >= 0)
        chilrens.value[currentChildIndex.value].click()
      else
        toggle(false)
    }
    if (event.key.toLowerCase() === 'arrowright' || event.key.toLowerCase() === 'arrowleft') {
      event.preventDefault()
      toggle(false)
    }
  }
})

watch(isOpen, (open) => {
  if (open)
    loadMenuControls()
})

onMounted(() => {
  loadMenuControls()
})

defineExpose({
  onKeyDown: (event: KeyboardEvent) => {
    if (event.key === 'Escape')
      toggle(false)
    if (event.key === 'ArrowDown')
      moveToNext()
    if (event.key === 'ArrowUp')
      moveToPrevious()
  },
  focus() {
    moveToNext()
  },
})
</script>

<template>
  <ul
    :id="`menu-${id}`"
    ref="wrapperRef"
    role="menu"
    :tabindex="isOpen ? 0 : -1"
    :aria-visible="isOpen"
    :aria-labelledby="`menu-button-${id}`"
    class="box-border bg-white rounded-lg border-1 border-gray-lighter min-w-24 p-1 animate-fade-in animate-forwards animate-duration-100 flex flex-col gap-1"
  >
    <slot :on-select="onSelect" />
  </ul>
</template>
